import { useIsFocused } from 'expo-router'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { Linking, View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Icon } from '@/src/elements/media/Icon'
import { Modal } from '@/src/elements/modal/Modal'
import { Typography } from '@/src/elements/text/Typography'
import { appStoreLink, playStoreLink } from '@/src/features/app/constants/storeLinks'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import type { TFullCommunityApi } from '@/src/features/community/api/communityApi.types'
import { useFraternityOnboardingQuery } from '@/src/features/fraternity/queries/useFraternityOnboardingQuery'
import {
  hasSeenDownloadAppModal,
  markDownloadAppModalAsSeen,
} from '@/src/features/fraternity/services/downloadAppModalStorage'
import { getIsNativeAppInstalled } from '@/src/features/fraternity/utils/getIsNativeAppInstalled'
import { withFraternity } from '@/src/features/fraternity/utils/withFraternity'
import { TEST_ID } from '@/src/general/constants/testId'
import { APP_TYPE } from '@/src/general/enums/appTypes'
import { ICON } from '@/src/general/enums/icons'
import { getAppType } from '@/src/general/utils/getAppType'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants/Events.enum'
import { isIOSWeb } from '@/src/general/utils/isIOSWeb'
import { useTranslate } from '@/src/lang/useTranslate'

/** Mobile web contexts where the native app can be pushed (EMULATION allows testing from desktop devtools) */
const ELIGIBLE_APP_TYPES = new Set([
  APP_TYPE.BROWSER_MOBILE,
  APP_TYPE.IOS_PWA,
  APP_TYPE.ANDROID_PWA,
  APP_TYPE.EMULATION,
])

const benefits = [
  { icon: ICON.BOOK_OPEN, messageKey: 'fraternity.download-app.benefit.content' as const },
  { icon: ICON.BELL, messageKey: 'fraternity.download-app.benefit.notifications' as const },
  { icon: ICON.USERS, messageKey: 'fraternity.download-app.benefit.fraternity' as const },
]

type TFraternityDownloadAppModalProps = {
  community?: Pick<TFullCommunityApi, 'tags'>
}

export const FraternityDownloadAppModal = ({ community }: TFraternityDownloadAppModalProps): ReactNode => {
  const [showModal, setShowModal] = useState(false)
  const [isEligibleAppType, setIsEligibleAppType] = useState(false)
  const hasOpened = useRef(false)
  const isFocused = useIsFocused()
  const { t } = useTranslate()
  const { data: me } = useMeQuery()

  useEffect(() => {
    setIsEligibleAppType(ELIGIBLE_APP_TYPES.has(getAppType()))
  }, [])

  const isWithFraternity = withFraternity(community)
  const { data: onboarding } = useFraternityOnboardingQuery({
    community,
    enabled: isEligibleAppType && isWithFraternity && !!me?.id,
  })

  const userId = me?.id
  const shouldDisplay = isFocused && isEligibleAppType && isWithFraternity && !!userId && !!onboarding?.requested_at

  useEffect(() => {
    if (!shouldDisplay || !userId || hasOpened.current || hasSeenDownloadAppModal(userId)) return
    hasOpened.current = true

    getIsNativeAppInstalled().then((isNativeAppInstalled) => {
      if (isNativeAppInstalled) return
      markDownloadAppModalAsSeen(userId)
      setShowModal(true)
      GTM.sendEvent(EVENTS.VIRTUS_DOWNLOAD_APP_DISPLAYED)
    })
  }, [shouldDisplay, userId])

  const handleDownload = () => {
    GTM.sendEvent(EVENTS.VIRTUS_DOWNLOAD_APP_CLICKED)
    setShowModal(false)
    Linking.openURL(isIOSWeb() ? appStoreLink : playStoreLink)
  }

  const handleClose = () => {
    GTM.sendEvent(EVENTS.VIRTUS_DOWNLOAD_APP_CLOSED)
    setShowModal(false)
  }

  return (
    <Modal
      testID={TEST_ID.FRATERNITY.DOWNLOAD_APP_MODAL}
      showModal={showModal}
      onCancel={handleClose}
      title={t('fraternity.download-app.title')}
      description={t('fraternity.download-app.description')}
      classNames={{ title: 'px-4 text-primary-page-title', body: 'gap-6 px-4' }}
    >
      <View className="gap-4">
        {benefits.map(({ icon, messageKey }) => (
          <View key={messageKey} className="flex-row items-center gap-4">
            <View className="rounded-lg bg-primary-bg-medium p-2">
              <Icon name={icon} size="md" className="text-primary-icon-contrast-lower" />
            </View>
            <Typography bold className="flex-1">
              <FormattedMessage id={messageKey} />
            </Typography>
          </View>
        ))}
      </View>
      <Typography size="sm">{t('fraternity.download-app.free')}</Typography>
      <View className="gap-3">
        <Button
          testID={TEST_ID.FRATERNITY.DOWNLOAD_APP_BUTTON}
          colors="primary"
          variant="solid"
          fullWidth
          onPress={handleDownload}
        >
          <FormattedMessage id="fraternity.download-app.cta" />
        </Button>
        <Button
          testID={TEST_ID.FRATERNITY.DOWNLOAD_APP_DISMISS_BUTTON}
          colors="primary"
          variant="flat"
          fullWidth
          onPress={handleClose}
        >
          <FormattedMessage id="fraternity.download-app.dismiss" />
        </Button>
      </View>
    </Modal>
  )
}
